import React, { useState, useEffect, useContext } from 'react';
import styles from './CreditCard.module.css';
import { WelletContext } from '../../context/WelletContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { Spinner } from '../Common/Spinner/Spinner';
import Modal from '../Common/Modal/Modal';
import Button from '../Common/Button/Button';
import { useTranslation } from 'react-i18next';
import DumpIcon from '../icons/DumpIcon';
import EmptyActivity from '../Common/EmptyActivity/EmptyActivity';
import CardBrandIcons from './CardBrandIcons';
import CardBrandBackgroundImage from './CardBrandBackgroundImage';

const CreditCardSeeList = ({ onCardIdChange }) => {
  const welletContext = useContext(WelletContext);
  const [currentCardId, setCurrentCardId] = useState(0);
  const [loadingCards, setLoadingCards] = useState(true);
  const [cards, setCards] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [selectedCardToken, setSelectedCardToken] = useState(null);
  const { t } = useTranslation();

  const deleteCard = async () => {
    if (!selectedCardToken) return;

    setLoadingCards(true);
    try {
      const response = await welletContext.apis.tickets.post(
        `/customer/removeCard?tokenCard=${selectedCardToken}`,
      );

      const updatedCards = await welletContext.apis.tickets.get('/customer/cards/');
      setCards(updatedCards.data);
      setLoadingCards(false);

      if (updatedCards.data && updatedCards.data.length > 0) {
        setCurrentCardId(0);
        onCardIdChange(updatedCards.data[0].token);
      } else {
        setCurrentCardId(0);
        onCardIdChange(null);
      }
    } catch (error) {
      console.error(error);
      setLoadingCards(false);
    }

    setShowConfirmDelete(false);
  };

  const getCards = async () => {
    setLoadingCards(true);
    try {
      const response = await welletContext.apis.tickets.get('/customer/cards/');
      setCards(response.data);

      if (response.data && response.data.length > 0) {
        setCurrentCardId(0);
        onCardIdChange(response.data[0].token);
      }
    } catch (error) {
      console.error(error);
    }
    setLoadingCards(false);
  };

  const onChangeSlide = (i) => {
    if (i >= cards.length) return;
    setCurrentCardId(i);
    onCardIdChange(cards[i].token);
  };

  const handleCardClick = (index) => {
    if (index !== currentCardId) {
      setCurrentCardId(index);
      onCardIdChange(cards[index].token);
    }
  };

  const handleDeleteClick = (token) => {
    setSelectedCardToken(token);
    setShowConfirmDelete(true);
  };
  
  const lastIndex = cards.length - 1;

  const cardStyle = (index, brand) => {
    return {
      backgroundImage: `url(${CardBrandBackgroundImage({ brand })})`,
      marginBottom: index === lastIndex ? '40px' : '10px',
      marginTop: index === 0 ? '20px' : '10px',
      transform: index === currentCardId ? 'scale(1.15)' : 'scale(1)',
      transition: 'transform 0.3s ease-in-out, margin-top 0.3s ease-in-out',
      opacity: index === currentCardId ? 1 : 0.6,    
    };
  };

  useEffect(() => {
    getCards();
  }, []);

  return (
    <>
      {showConfirmDelete && (
        <Modal title={t('confirmAction')} onClose={() => setShowConfirmDelete(false)} hasBackground>
          <div className='text-white text-center fw-semibold'>{t('deleteCard')}</div>
          <Button size='medium' text={t('confirm')} onClick={deleteCard} className='mt-4' />
          <Button
            size='medium'
            text={t('cancel')}
            onClick={() => setShowConfirmDelete(false)}
            className=' mt-2'
            backgroundColor='#fff'
            color='#000'
          />
        </Modal>
      )}

      <div className={`${styles.sliderContainer}`}>
        <Swiper
         cssMode={true}
          loop={false}
          direction='vertical'
          spaceBetween={10}
          slidesPerView={'auto'}
          onSlideChange={(e) => onChangeSlide(e.activeIndex)}
        >
          {loadingCards ? (
            <div
              className='d-flex justify-content-center align-items-center'
              style={{ height: '80vh' }}
            >
              <Spinner />
            </div>
          ) : (
            <>
              {cards.length > 0 ? (
                cards.map(
                  (
                    { id, last4, brand, country, expiryMonth, expiryYear, cardholderName },
                    index,
                  ) => (
                    <SwiperSlide key={id}>
                      <div
                        className={styles.card}
                        style={cardStyle(index, brand)}
                        onClick={() => handleCardClick(index)}
                      >
                        <div className={styles.delantera}>
                          <div
                            className={`${styles.logoYChip} ${
                              index === currentCardId ? styles.selected : ''
                            }`} 
                          >
                            <CardBrandIcons brand={brand} />
                            {index === currentCardId && (
                              <div
                                className='text-end'
                                style={{
                                  marginBottom: '20px',
                                  cursor: 'pointer',
                                  marginLeft: 'auto',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(cards[index].token);
                                }}
                              >
                                <DumpIcon color={'#fff'} size={19} />
                              </div>
                            )}
                          </div>

                          <div className='mt-3'>
                            <div className={styles.grupo}>
                              <p className={styles.numero}>**** **** **** **** {last4}</p>
                            </div>
                            <div className={styles.flexbox}>
                              <div className={styles.grupo}>
                                <p className={styles.label}>Card Holder Name</p>
                                <p className={styles.nombre}>{cardholderName}</p>
                              </div>
                              <div className={styles.grupo}>
                                <p className={styles.label}>Expiry Date</p>
                                <p className={styles.expiracion}>
                                  <span className={styles.mes}>
                                    {expiryMonth.toString().padStart(2, '0')}
                                  </span>{' '}
                                  / <span className={styles.year}>{expiryYear}</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ),
                )
              ) : (
                <div className=' m-3'>
                  <EmptyActivity title={t('noCardsAvailable')} />
                </div>
              )}
            </>
          )}
        </Swiper>
      </div>
    </>
  );
};

export default CreditCardSeeList;
